/**
 * Events emitted during the lifecycle of the player.
 *
 * @remarks
 * Events can be subscribed to with `player.addEventListener()`.
 *
 * Ad related events use {@link AdvertisingEvent}.
 *
 * Many events are not correctly written in the past tense. This should be fixed in a future version to avoid confusion.
 */
export const PlayerEvent = {
  /**
   * The player has been created.
   *
   * @remarks
   * This even bubbles through the DOM and can be subscribed to on the document.
   * The player can be accessed with `event.target.getPlayer()`
   *
   * Use this event to initialize your player UI and register event listeners.
   */
  PlayerCreated: 'player:PlayerCreated',

  /**
   * The media started to play.
   *
   * @remarks
   * This event is emitted once per media. It will fire either when the content or an ad (pre-roll) starts.
   * When a pre-roll starts, it will not fire again for the content.
   * When a new media is loaded, it will fire again for that media.
   */
  MediaStarted: 'player:MediaStarted',

  /**
   * This event is emitted the first time a content frame was played for the current manifest.
   *
   * @remarks
   * If Pre-Rolls are configured this will happen after the ad slot finished.
   *
   * Use this event to start content tracking and switch your UI to content mode.
   *
   * @privateRemarks
   * This event should be called ContentStarted instead in a future version.
   */
  ContentStart: 'player:ContentStart',

  /**
   * This event is emitted after the content finished.
   *
   * @remarks
   * If Post-Rolls are configured they will play after ContentEnd was emitted.
   *
   * Use this event to stop content tracking.
   *
   * If you implement a playlist, this is **not** the event to start the next item.
   * Use {@link PlayerEvent.SessionEnd} instead.
   *
   * @privateRemarks
   * This event should be called ContentEnded or Finished instead in a future version.
   */
  ContentEnd: 'player:ContentEnd',

  /**
   * The player will start playout.
   *
   * @remarks
   * The player will emit either an AdSlotStart or ContentStart event next,
   * depending on the provided configuration.
   *
   * Use this event to start your loading indicator.
   * This is also the first event for every media session, which can be used to start tracking sessions.
   *
   * @privateRemarks
   * This event should be called SessionStarted instead in a future version.
   */
  SessionStart: 'player:SessionStart',

  /**
   * The player finished playing the content and every ad slot.
   *
   * @remarks
   * This event is emitted after the last ad slot finished. If no post-roll is configured, this event is emitted after ContentEnd.
   *
   * While calling `play()`, `seek()` and other methods of the player after the
   * session ended might work, tracking won't. Neither will another SessionEnd
   * event be emitted when the content or ad slot ended another time.
   *
   * Use this event to start the next item in your playlist.
   * Use this event to show a replay button. A replay must start a new session
   * by calling `loadMedia` again.
   *
   * @privateRemarks
   * This event should be called SessionEnded or Finished instead in a future version.
   */
  SessionEnd: 'player:SessionEnd',

  /**
   * Player will be destroyed and must not be used anymore.
   *
   * @remarks
   * This will emit an event of type FoundationDestroy.
   *
   * If the player is being destroyed due to an error, the `error` property will be populated with the relevant error.
   *
   * You should register a listener for this event and tear down all your player related logic and the UI.
   * If you do not listen on this event, the player will be destroyed regardless.
   *
   * The player will also remove all event listeners, so you do not have to worry about memory leaks.
   *
   * You should display the error message to the user, if the player was destroyed due to an error.
   * You should not listen on the {@link PlayerEvent.Error} event itself and destroy the player manually,
   * because the player may try to recover playback first and will ultimately destroy itself if needed.
   */
  Destroy: 'player:Destroy',

  /**
   * Player falls back to a SD manifest and tries to load it.
   *
   * @remarks
   * This happens after an error occurred, while loading the HD manifest and if a SD fallback manifest is supplied.
   *
   * This happens most commonly when the user is recording the screen,
   * or the device is not connected via an HDCP compliant HDMI cable.
   * This can also happen if the device is not HDCP compliant at all e.g. custom hardware like a Raspberry Pi.
   *
   * @see https://en.wikipedia.org/wiki/High-bandwidth_Digital_Content_Protection
   */
  HDCPFallback: 'player:HDCPFallback',

  /**
   * A subtitle should be displayed.
   *
   * @remarks
   * This event only happens if the player is configured to display subtitles.
   */
  CueEnter: 'player:CueEnter',

  /**
   * A subtitle should be removed.
   *
   * @remarks
   * This event happens if the player is configured to display subtitles and the subtitles should be cleared.
   * This event may also happen if subtitles were enabled and are disabled now.
   */
  CueExit: 'player:CueExit',

  /**
   * An error occurred in the player.
   *
   * @remarks
   * An error may happen for a multitude of reasons, e.g.: DRM, HDCP, Misconfiguration, Network, etc.
   * Not every error is fatal and the player may recover from some errors.
   * The player may also recover from some fatal errors automatically.
   *
   * You should not listen to this event and destroy the player manually.
   * Instead, listen to {@link PlayerEvent.Destroy} and display an error message to the user if the error property is populated.
   *
   * If you destroy the player manually, the player cannot recover from the error,
   * which will lead to a bad user experience.
   *
   * You can listen to this event for debugging purposes, especially if the player does not destroy itself.
   * Those cases should be reported to the player team for further investigation.
   */
  Error: 'player:Error',

  /**
   * The player has been muted.
   *
   * @remarks
   * This event is **not** initially emitted if player has been muted by setting `muted` in the media config.
   *
   * This event is only emitted if the player is muted. If the tab is muted, but not the player directly,
   * this event will not be emitted.
   *
   * The UI should have separate mute and volume controls.
   * The volume control should be updated on {@link PlayerEvent.VolumeChanged}.
   * The mute control should be updated on this event and {@link PlayerEvent.Unmuted}.
   */
  Muted: 'player:Muted',

  /**
   * The player has been unmuted.
   *
   * @remarks
   * If the tab was unmuted, but not the player directly, this event will not be emitted.
   */
  Unmuted: 'player:Unmuted',

  /**
   * play() has been called on the player.
   *
   * @remarks
   * This event follows roughly the same rules as the native play event.
   * Unfortunately, players and browsers are not consistent in their implementation,
   * so we cannot guarantee that this event is 100% consistent across the board.
   *
   * For UI purposes this is the event you can use to change the status of your play/pause button.
   *
   * To display a loading or waiting state it is recommended to listen on {@link PlayerEvent.Playing} instead.
   * {@link PlayerEvent.Playing} will emit for every state change and not just manual ones.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/play_event
   */
  Play: 'player:Play',

  /**
   * pause() has been called on the player.
   *
   * @remarks
   * This event follows roughly the same rules as the native pause event.
   * Unfortunately, players and browsers are not consistent in their implementation,
   * so we cannot guarantee that this event is 100% consistent across the board.
   *
   * For UI purposes this is the event you can use to change the status of your play/pause button.
   *
   * But note that this event will only be emitted when the player has been playing at the time of the pause call,
   * i.e. if there are two `player.pause()` calls right after each other, there won't be an emitted event for the
   * second call.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/pause_event
   */
  Paused: 'player:Paused',

  /**
   * The playback speed has changed.
   *
   * @remarks
   * This mostly occurs when PlayerInterface::setPlaybackSpeed() is called.
   *
   * This may also happen if the user changed the playback speed and the player switched from content playback to ad playback.
   * In those cases the player will try to maintain a playback speed of x1.
   * THe player will switch back to the desired speed once the player switches back to the content.
   */
  PlaybackSpeedChanged: 'player:PlaybackSpeedChanged',

  /**
   * Player changed into the `playing` state.
   *
   * @remarks
   * This event follows roughly the same rules as the native playing event.
   * Unfortunately, players and browsers are not consistent in their implementation,
   * so we cannot guarantee that this event is 100% consistent across the board.
   *
   * In general this happens when the player changes from another state to the playing state.
   * Some of the reasons may be:
   * - The player was paused and play was called
   * - The player started playback
   * - The player was buffering and has enough data to play again
   * - The player was seeking and has reached the new position with enough data to play again
   *
   * Listen for {@link PlayerEvent.Paused}, {@link PlayerEvent.ContentEnd} and {@link PlayerEvent.SessionEnd} to understand when the player stops playing content.
   * Listen for {@link PlayerEvent.Play}, {@link PlayerEvent.ContentStart} and {@link PlayerEvent.Playing} to understand when the player starts playing content.
   * Listen for {@link PlayerEvent.StallStarted} to understand when the player starts stalling.
   * Listen for {@link PlayerEvent.Seek} and {@link PlayerEvent.Seeked} to understand when the player seeks and finished seeking.
   *
   * Use this event to hide your loading or waiting indicator in your UI.
   *
   * @see https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/playing_event
   */
  Playing: 'player:Playing',

  /**
   * Player is ready for playback (Content and Ads ready)
   *
   * @remarks
   * Once this event is emitted it is safe to call player.play() to start playback.
   *
   * Use this event to hide your loading or waiting indicator in your UI.
   */
  Ready: 'player:Ready',

  /**
   * Player is seeking to a new position
   *
   * @remarks
   * This event is almost exclusively emitted when the user seeks.
   *
   * There is one notable exception:
   * If the player is buffering and switches back into the playing state, the player may sometimes not start playing.
   * In those cases the player will trigger a seek to the current position + 0.1s. This is a workaround for a hardware bug.
   * We currently only do this on MagentaTV devices, but could be extended to other devices in the future.
   *
   * For the UI it is recommended to show a loading or waiting indicator on this event.
   */
  Seek: 'player:Seek',

  /**
   * Player finished seeking to a new position
   *
   * @remarks
   * This event is emitted after the {@link PlayerEvent.Seek} event.
   *
   * The UI should hide the loading or waiting indicator on this event.
   */
  Seeked: 'player:Seeked',

  /**
   * The player started stalling and cannot play the current content.
   *
   * @remarks
   * This could be due to multiple reasons, e.g.: Network, Hardware, Memory, etc.
   * The player will try to recover from the stall and will emit {@link PlayerEvent.StallEnded} once the player can play again.
   *
   * When this event is emitted, the UI should show a loading or waiting indicator.
   */
  StallStarted: 'player:StallStarted',

  /**
   * The player is no longer stalling and playback will continue.
   *
   * @remarks
   * When this event is emitted, the UI should hide the loading or waiting indicator.
   */
  StallEnded: 'player:StallEnded',

  /**
   * This is emitted often (a few times per second) when playback is in progress.
   *
   * @remarks
   * This event is emitted often enough to be used for playback tracking.
   * Depending on your needs you may want to throttle the event.
   *
   * The seekbar of your UI should be updated on this event.
   * It's recommended to use this event instead of requestAnimationFrame or a custom setInterval,
   * because you will have to call player.getCurrentTime() anyway and cause unnecessary overhead.
   */
  TimeChanged: 'player:TimeChanged',

  /**
   * Volume of the player was changed
   *
   * @remarks
   * The UI should have separate mute and volume controls.
   * The volume control should be updated on this event.
   * The mute control should be updated on {@link PlayerEvent.Muted} and {@link PlayerEvent.Unmuted}.
   */
  VolumeChanged: 'player:VolumeChanged',

  /**
   * Another audio track has been selected for playout.
   */
  AudioTrackSelected: 'player:AudioTrackSelected',

  /**
   * The active state of the current TextTrack has changed,
   * or other TextTracks have been selected and are now used for playout.
   */
  TextTrackSelected: 'player:SubtitlesSelected',

  /**
   * @deprecated please use TextTrackSelected
   */
  SubtitlesSelected: 'player:SubtitlesSelected',

  /**
   * The user has provided new metadata for the current media.
   */
  MetadataUpdated: 'player:MetadataUpdated',
} as const;

/**
 * {@inheritDoc PlayerEvent:var}
 */
export type PlayerEvent = (typeof PlayerEvent)[keyof typeof PlayerEvent];
